import {RELATED_SECTION_TITLES} from '../constants';

/** Slugs the markdown links to: `[[slug]]`, `[[slug|text]]`, `](/help/slug)`. */
export function linkedSlugs(markdown: string): Set<string> {
  const slugs = new Set<string>();
  for (const match of markdown.matchAll(/\[\[([^|\]]+)(?:\|[^\]]+)?\]\]/g)) {
    slugs.add(match[1].trim());
  }
  for (const match of markdown.matchAll(
    /\]\((?:https?:\/\/smartmixin\.io)?\/help\/(?:[a-z]{2}\/)?([a-z0-9-]+)[)#?]/g,
  )) {
    slugs.add(match[1]);
  }
  return slugs;
}

/**
 * Where an article ends with its own "Related articles" h2 (most do), the
 * frontmatter `related:` slugs the page links nowhere are appended to that
 * section as wiki-links, so the page carries one related section and every
 * related slug. Without such a section the content is returned unchanged and
 * `hasSection` is false: the caller appends the generated list.
 *
 * `extraTitles` adds the UI label of the reader's language to the
 * per-locale titles in RELATED_SECTION_TITLES (compared lower-cased).
 */
export function mergeRelatedIntoBody(
  content: string,
  related: string[],
  extraTitles: string[] = [],
): {content: string; hasSection: boolean} {
  const titles = new Set(
    [...RELATED_SECTION_TITLES, ...extraTitles].map((t) => t.toLowerCase()),
  );
  const lines = content.split('\n');
  const start = lines.findIndex((line) => {
    const match = /^##\s+(.+?)\s*#*\s*$/.exec(line);
    return !!match && titles.has(match[1].trim().toLowerCase());
  });
  if (start === -1) {
    return {content, hasSection: false};
  }
  const linked = linkedSlugs(content);
  const missing = related.filter((slug) => !linked.has(slug));
  if (missing.length === 0) {
    return {content, hasSection: true};
  }
  let end = lines.findIndex((line, i) => i > start && /^##?\s/.test(line));
  if (end === -1) end = lines.length;
  // After the section's last non-blank line, so the new items join its list.
  let insertAt = end;
  while (insertAt > start + 1 && lines[insertAt - 1].trim() === '') insertAt--;
  const items = missing.map((slug) => `- [[${slug}]]`);
  lines.splice(insertAt, 0, ...items);
  return {content: lines.join('\n'), hasSection: true};
}
